import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import ReferringDentistsTable from '@/components/ReferringDentistsTable';

/**
 * Standalone admin page wrapper for the referring-dentist directory.
 *
 * As of #19 the primary entry point for this CRUD moved into the Patients
 * view as a sub-tab. This page is kept as a deep-link fallback (e.g. links
 * from the Admin landing or saved bookmarks). All real logic lives in the
 * shared `ReferringDentistsTable` component used by both surfaces.
 */
const AdminReferringDentists = () => {
  const { canAccessAdminPanel } = useAuth();
  const navigate = useNavigate();

  if (!canAccessAdminPanel()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access denied</h2>
          <p className="text-gray-500">
            The referring-dentist directory is restricted to admin users.
          </p>
          <Button className="mt-4" onClick={() => navigate('/admin')}>
            Back to admin
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header + breadcrumb */}
      <div className="space-y-2">
        <nav className="text-xs sm:text-sm text-slate-500 flex items-center gap-1">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-slate-900 hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" />
            Admin
          </button>
          <span>/</span>
          <span className="text-slate-900 font-medium">Referring Dentists</span>
        </nav>
      </div>

      <ReferringDentistsTable variant="admin" enabled={canAccessAdminPanel()} />
    </div>
  );
};

export default AdminReferringDentists;
